import { useObservable } from '@streem/sdk-react';
import { styled } from '@streem/ui-react';
import { FC, PropsWithChildren } from 'react';
import { WallItem } from 'streem-sdk-protobuf';
import { useDetailSession } from '../../hooks/detail_session_hooks';
import { gpsPosToGoogleMaps } from '../../util/routing';
import { DecoratedLink } from '../links/decorated_link';
import { Subheader } from '../typography/typography';

/**
 * Displays the GPS location of a call as a decorated link
 * Links to google maps
 */
export const CallDetailsLocation: FC = () => {
    const detailSession = useDetailSession();

    const [latestLocation] = useObservable(detailSession.geolocation.latestLocation);

    return (
        <LocationWrapper>
            <Subheader size="mediumLarge" data-testid="call-details-location-heading">
                Location
            </Subheader>
            <LocationLink latestGpsPosition={latestLocation?.gpsPosition ?? undefined} />
        </LocationWrapper>
    );
};

/**
 * Returns formatted address & coordinates OR just coodinates depending on company setting
 *
 * @param latestGpsPosition - GPS coordinates associated with the call
 */
const LocationLink: FC<
    PropsWithChildren<{
        latestGpsPosition?: WallItem.IGPSPosition;
    }>
> = ({ latestGpsPosition }) => {
    if (!latestGpsPosition) {
        return (
            <DecoratedLink
                data-testid="call-details-location-link"
                icon={'GPSIcon'}
                displayText="No GPS location found"
            />
        );
    }

    return (
        <address aria-label={latestGpsPosition.formattedAddress ?? ''}>
            <DecoratedLink
                data-testid="call-details-location-link"
                icon={'GPSIcon'}
                displayText={latestGpsPosition.formattedAddress ?? ''}
                link={gpsPosToGoogleMaps(latestGpsPosition)}
                subText={formattedCoordinates(latestGpsPosition)}
            />
        </address>
    );
};

const formattedCoordinates = (gpsPosition: WallItem.IGPSPosition) => {
    return `${gpsPosition.latitude?.toFixed(5)}, ${gpsPosition.longitude?.toFixed(5)}`;
};

const LocationWrapper = styled.div`
    margin-bottom: 24px;
`;
